<template>
	<component
		ref="cta"
		:is="tagName"
		:to="link"
		:class="['cta-btn', ctaColor]"
		@click="click"
		@touchstart="emitClickIntent"
		@mouseover="emitClickIntent"
		:disabled="ctaDisabled"
		:aria-label="ctaAriaLabel"
	>
		<span>
			{{ ctaLabel }}
		</span>
		<SvgIcon v-if="ctaIcon" :icon="ctaIcon" class="cta-btn__icon" />
	</component>
</template>

<script setup>
const emit = defineEmits(["button-click", "button-click-intent"]);

const props = defineProps({
	fields: { type: Object, required: false },
	rendering: { type: Object, required: false },
	ctaLink: { type: Object, required: false },
	icon: { type: String, required: false, default: undefined },
	color: { type: String, required: false },
	gtm: { type: String, required: false },
	modal: { type: Object, required: false },
	disabled: { type: Boolean, required: false, default: false },
	ariaLabel: { type: String, required: false, default: undefined },
});

const link = computed(() => {
	if (ctaDisabled.value) return undefined;

	return props.fields?.ctaLink?.value?.url
		? props.fields.ctaLink.value.url
		: props.ctaLink?.url;
});

const ctaIcon = computed(() => {
	return props.fields?.ctaIcon?.value || props.icon;
});

const tagName = computed(() =>
	link.value && !ctaModal.value && !ctaDisabled.value
		? resolveComponent("NuxtLinkLocale")
		: "button"
);

const ctaLabel = computed(() =>
	props.fields?.ctaLink?.value
		? props.fields.ctaLink.value.text
		: props.ctaLink?.text
);

const ctaColor = computed(() =>
	props.fields?.ctaColor?.value ? props.fields.ctaColor.value : props.color
);

const ctaGtm = computed(() =>
	props.fields?.ctaGtm?.value ? props.fields.ctaGtm.value : props.gtm
);

const ctaModal = computed(() =>
	props.fields?.ctaModal ? props.fields.ctaModal : props.modal
);

const ctaDisabled = computed(() => {
	if (props.disabled) return true;
	if (props.fields?.ctaDisabled?.value) return props.fields.ctaDisabled.value;
	return false;
});

const ctaAriaLabel = computed(() => {
	if (props.fields?.ctaAriaLabel?.value) return props.fields.ctaAriaLabel.value;
	else if (props.ariaLabel) return props.ariaLabel;
	else return ctaLabel.value;
});

const { pushGa4 } = useTracking();

function click() {
	pushGa4(ctaGtm.value);
	emit("button-click");

	if (ctaModal.value && ctaModal.value.fields) {
		document.dispatchEvent(
			new CustomEvent("open-modal", {
				detail: { fields: ctaModal.value.fields },
			})
		);
	}
}

function emitClickIntent() {
	emit("button-click-intent");
}
</script>

<style lang="scss">
.component--defaultbutton {
	padding: var(--spacing-2, 16px);
}

.cta-btn {
	span {
		width: 100%;
	}

	&:hover,
	&:focus {
		color: var(--color-white, #fff);
		border-color: var(--color-white, #fff);

		.cta-btn__icon path {
			fill: var(--color-white, #fff);
		}

		&:before {
			width: 100%;
		}
	}

	&:disabled {
		opacity: 0.5 !important;
		cursor: not-allowed !important;
		pointer-events: none !important;
	}

	.cta-btn__icon path {
		fill: var(--color-white, #fff);
	}

	&.cta-btn--full-width {
		width: 100%;
	}

	&.cta-btn--margin-bottom {
		margin-bottom: var(--spacing-2, 16px);
	}

	&,
	&.cta-btn--light,
	&.light {
		border-color: var(--color-white, #fff);
		background-color: var(--color-white, #fff);
		color: #1b1b1b;

		.cta-btn__icon path {
			fill: #1b1b1b;
		}

		&:hover,
		&:active,
		&:focus-visible {
			border-color: #1b1b1b;
			background-color: #1b1b1b;
			color: var(--color-white, #fff);
		}
	}

	&.ghost-light {
		background-color: transparent;
		color: var(--color-white, #fff);
		border-color: var(--color-white, #fff);

		.cta-btn__icon path {
			fill: var(--color-white, #fff);
		}

		&:hover,
		&:active,
		&:focus-visible {
			background-color: rgba(255, 255, 255, 0.35);
		}
	}

	&.dark {
		border-color: var(--color-black, #000);
		background-color: var(--color-black, #000);
		color: var(--color-white, #fff);

		.cta-btn__icon path {
			fill: var(--color-white, #fff);
		}

		&:hover,
		&:active,
		&:focus-visible {
			border-color: var(--color-white, #fff);
			background-color: var(--color-white, #fff);
			color: var(--color-black, #000);

			.cta-btn__icon path {
				fill: var(--color-black, #000);
			}
		}
	}

	&.ghost-dark {
		background-color: transparent;
		color: var(--color-black, #000);
		border-color: var(--color-black, #000);

		&:hover,
		&:active,
		&:focus-visible {
			background-color: #cbcbcb;
		}
	}

	&.primary {
		border-color: var(--primary-color, #fd470b);
		background-color: var(--primary-color, #fd470b);
		color: var(--color-white, #fff);

		.cta-btn__icon path {
			fill: var(--color-white, #fff);
		}

		&:hover,
		&:active,
		&:focus-visible {
			border-color: var(--color-white, #fff);
			background-color: var(--color-white, #fff);
			color: var(--color-white, #fff);

			.cta-btn__icon path {
				fill: var(--color-white, #fff);
			}
		}
	}

	&.ghost-primary {
		background-color: transparent;
		color: var(--primary-color, #000);
		border-color: var(--primary-color, #000);

		&:hover,
		&:active,
		&:focus-visible {
			background-color: #cbcbcb;
		}
	}

	&:focus-visible {
		outline: 2px solid var(--color-btn-focus, #015ecc);
		outline-offset: 4px;
	}

	&.standalone-light {
		margin: var(--spacing-2, 16px);
		border-color: transparent;
		color: var(--color-white, #fff);
		text-decoration: underline;
		font-size: var(--text-xs, 12px);
		font-weight: 700;
		line-height: 140%;

		& > span {
			width: auto;
			padding-left: var(--spacing-4, 32px);
		}

		.cta-btn__icon {
			height: 14px;
		}
	}
}
</style>
